import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  BasketBook,
  Book,
  BookFilters,
  BookRaw,
  CreateBook,
} from '../types/book'
import { bookApi, commentApi } from '../api/api'
import { RateForm } from '../types/rating'
import {
  Comment,
  CommentForm,
  CommentRaw,
  EvaluationForm,
} from '../types/comment'
import { convertCommentRawToComment } from '../utils/comment/convertCommentRawToComment'
// import {
//   CalendarBook,
//   CreateBook,
//   Book,
//   UpcomingBooks,
// } from '../types/book'
// import { loadBooks, modifyBook, removeBook, saveBook } from '../api/book'
// import { findCurrentTimePeriodBooks } from '../utils/findCurrentTimePeriodBooks'
// import { isWithinInterval } from 'date-fns'
// import { findBooksByDate } from '../utils/findBooksByDate'

// get books by category
export const loadBooksByCategory = createAsyncThunk(
  'book/loadBooksByCategory',
  async (category: string, thunkApi) => {
    try {
      return await bookApi.getBooksByCategory(category)
    } catch (e) {
      return thunkApi.rejectWithValue('getBooksError')
    }
  },
)

// get new books
export const loadNewBooks = createAsyncThunk(
  'book/loadNewBooks',
  async (count: number, thunkApi) => {
    try {
      return await bookApi.getNewBooks(count)
    } catch (e) {
      return thunkApi.rejectWithValue('loadNewBooksError')
    }
  },
)

// get new books
export const loadPopularBooks = createAsyncThunk(
  'book/loadPopularBooks',
  async (_, thunkApi) => {
    try {
      return await bookApi.getPopularBooks()
    } catch (e) {
      return thunkApi.rejectWithValue('loadPopularBooksError')
    }
  },
)

// load book
export const loadBookById = createAsyncThunk(
  'book/loadBookById',
  async (id: string, thunkApi) => {
    try {
      return await bookApi.getBook(id)
    } catch (e) {
      return thunkApi.rejectWithValue('loadBookByIdError')
    }
  },
)

// rate book
export const rateBook = createAsyncThunk(
  'book/rateBook',
  async (rateForm: RateForm, thunkApi) => {
    try {
      return await bookApi.rateBook(rateForm)
    } catch (e) {
      return thunkApi.rejectWithValue('rateBookError')
    }
  },
)

// comment book
export const commentBook = createAsyncThunk(
  'book/commentBook',
  async (commentForm: CommentForm, thunkApi) => {
    try {
      return await bookApi.commentBook(commentForm)
    } catch (e) {
      return thunkApi.rejectWithValue('commentBookError')
    }
  },
)

// evaluate book
export const evaluateComment = createAsyncThunk(
  'book/evaluateComment',
  async (evaluationForm: EvaluationForm, thunkApi) => {
    try {
      return await commentApi.evaluateComment(evaluationForm)
    } catch (e) {
      return thunkApi.rejectWithValue('evaluateCommentError')
    }
  },
)

// unEvaluate book
export const unEvaluateComment = createAsyncThunk(
  'book/unEvaluateComment',
  async (commentId: string, thunkApi) => {
    try {
      return await commentApi.unEvaluateComment(commentId)
    } catch (e) {
      return thunkApi.rejectWithValue('unEvaluateCommentError')
    }
  },
)

// delete comment
export const deleteComment = createAsyncThunk(
  'book/deleteComment',
  async (id: string, thunkApi) => {
    try {
      return await commentApi.deleteComment(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteCommentError')
    }
  },
)

// add book
export const addBook = createAsyncThunk(
  'book/addBook',
  async (book: CreateBook, thunkApi) => {
    try {
      return await bookApi.createBook(book)
    } catch (e) {
      return thunkApi.rejectWithValue('addBookError')
    }
  },
)

// load basket books
export const loadBasketBooks = createAsyncThunk(
  'book/loadBasketBooks',
  async (_, thunkApi) => {
    try {
      return await bookApi.getBasketBooks()
    } catch (e) {
      return thunkApi.rejectWithValue('loadBasketBooksError')
    }
  },
)

// add book to basket
export const addBookToBasket = createAsyncThunk(
  'book/addBookToBasket',
  async (bookId: string, thunkApi) => {
    try {
      return await bookApi.addBookToBasket(bookId)
    } catch (e) {
      return thunkApi.rejectWithValue('addBookToBasketError')
    }
  },
)

// remove book from basket
export const removeBookFromBasket = createAsyncThunk(
  'book/removeBookFromBasket',
  async (bookId: string, thunkApi) => {
    try {
      return await bookApi.removeBookFromBasket(bookId)
    } catch (e) {
      return thunkApi.rejectWithValue('removeBookFromBasketError')
    }
  },
)

// // update book
// export const updateBook = createAsyncThunk(
//   'book/updateBook',
//   async (book: Book, thunkApi) => {
//     try {
//       return await modifyBook(book)
//     } catch (e) {
//       return thunkApi.rejectWithValue('updateBookError')
//     }
//   },
// )

// // delete objective
// export const deleteBook = createAsyncThunk(
//   'book/deleteBook',
//   async (id: string, thunkApi) => {
//     try {
//       return await removeBook(id)
//     } catch (e) {
//       return thunkApi.rejectWithValue('deleteBookError')
//     }
//   },
// )

interface BookSliceState {
  books: Book[]
  newBooks: Book[]
  popularBooks: Book[]
  basketBooks: BasketBook[]
  book?: Book
  filters: BookFilters
  loading: boolean
  error?: SerializedError
}

const initialState: BookSliceState = {
  books: [],
  newBooks: [],
  popularBooks: [],
  basketBooks: [],
  book: undefined,
  filters: {
    name: '',
  },
  loading: true,
  error: undefined,
}

const bookSlice = createSlice({
  name: 'book',
  initialState,
  reducers: {
    setFilters: (state: BookSliceState, action: PayloadAction<BookFilters>) => {
      state.filters = action.payload
    },
    setBook: (
      state: BookSliceState,
      action: PayloadAction<Book | undefined>,
    ) => {
      state.book = action.payload
    },
    setBooksToBasket: (
      state: BookSliceState,
      action: PayloadAction<BasketBook[]>,
    ) => {
      state.basketBooks = [...action.payload]
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<any>) => {
    // get load books by category
    // @ts-ignore
    builder.addCase(
      loadBooksByCategory.fulfilled,
      (state: BookSliceState, action: PayloadAction<Book[]>) => {
        state.books = action.payload
        state.loading = false
        state.error = undefined
      },
    )

    builder.addCase(
      loadBooksByCategory.pending,
      (state: BookSliceState, action) => {
        state.loading = true
        state.error = undefined
      },
    )

    builder.addCase(loadBooksByCategory.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    // get load new books
    // @ts-ignore
    builder.addCase(
      loadNewBooks.fulfilled,
      (state: BookSliceState, action: PayloadAction<Book[]>) => {
        state.newBooks = action.payload
        state.error = undefined
      },
    )

    builder.addCase(loadNewBooks.pending, (state: BookSliceState, action) => {
      state.error = undefined
    })

    builder.addCase(loadNewBooks.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // get load book by id
    // @ts-ignore
    builder.addCase(
      loadBookById.fulfilled,
      (state: BookSliceState, action: PayloadAction<BookRaw>) => {
        state.book = {
          ...action.payload,
          comments: action.payload.comments.map((comment: CommentRaw) =>
            convertCommentRawToComment(comment),
          ),
        }
        state.error = undefined
        state.loading = false
      },
    )

    builder.addCase(loadBookById.pending, (state: BookSliceState, action) => {
      state.error = undefined
      state.loading = true
    })

    builder.addCase(loadBookById.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
      state.loading = false
    })

    // rate book
    // @ts-ignore
    builder.addCase(
      rateBook.fulfilled,
      (state: BookSliceState, action: PayloadAction<number>) => {
        state.book!.averageRating = action.payload
        state.error = undefined
      },
    )

    builder.addCase(rateBook.pending, (state: BookSliceState, action) => {
      state.error = undefined
    })

    builder.addCase(rateBook.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // comment book
    // @ts-ignore
    builder.addCase(
      commentBook.fulfilled,
      (state: BookSliceState, action: PayloadAction<CommentRaw>) => {
        state.book!.comments = [
          ...state.book!.comments,
          convertCommentRawToComment(action.payload),
        ]
        state.error = undefined
      },
    )

    builder.addCase(commentBook.pending, (state: BookSliceState, action) => {
      state.error = undefined
    })

    builder.addCase(commentBook.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // evaluate comment
    // @ts-ignore
    builder.addCase(
      evaluateComment.fulfilled,
      (state: BookSliceState, action: PayloadAction<CommentRaw>) => {
        const updatedComment = convertCommentRawToComment(action.payload)
        state.book!.comments = state.book!.comments.map((comment) =>
          comment.id === updatedComment.id ? updatedComment : comment,
        )

        state.error = undefined
      },
    )

    builder.addCase(
      evaluateComment.pending,
      (state: BookSliceState, action) => {
        state.error = undefined
      },
    )

    builder.addCase(evaluateComment.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // unEvaluate comment
    // @ts-ignore
    builder.addCase(
      unEvaluateComment.fulfilled,
      (state: BookSliceState, action: PayloadAction<CommentRaw>) => {
        const updatedComment = convertCommentRawToComment(action.payload)
        state.book!.comments = state.book!.comments.map((comment) =>
          comment.id === updatedComment.id ? updatedComment : comment,
        )
        state.error = undefined
      },
    )

    builder.addCase(
      unEvaluateComment.pending,
      (state: BookSliceState, action) => {
        state.error = undefined
      },
    )

    builder.addCase(unEvaluateComment.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // delete comment
    // @ts-ignore
    builder.addCase(
      deleteComment.fulfilled,
      (state: BookSliceState, action: PayloadAction<string>) => {
        state.book!.comments = state.book!.comments.filter(
          (comment) => comment.id !== action.payload,
        )
        state.error = undefined
      },
    )

    builder.addCase(deleteComment.pending, (state: BookSliceState, action) => {
      state.error = undefined
    })

    builder.addCase(deleteComment.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // get load popular books
    // @ts-ignore
    builder.addCase(
      loadPopularBooks.fulfilled,
      (state: BookSliceState, action: PayloadAction<Book[]>) => {
        state.popularBooks = action.payload
        state.error = undefined
      },
    )

    builder.addCase(
      loadPopularBooks.pending,
      (state: BookSliceState, action) => {
        state.error = undefined
      },
    )

    builder.addCase(loadPopularBooks.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // Add book
    // @ts-ignore
    builder.addCase(
      addBook.fulfilled,
      (state: BookSliceState, action: PayloadAction<Book>) => {
        state.books = { ...state.books, ...action.payload }
        state.loading = false
        state.error = undefined
      },
    )

    builder.addCase(addBook.pending, (state: BookSliceState, action) => {
      state.loading = true
      state.error = undefined
    })

    builder.addCase(addBook.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    // load basket books
    // @ts-ignore
    builder.addCase(
      loadBasketBooks.fulfilled,
      (state: BookSliceState, action: PayloadAction<BasketBook[]>) => {
        state.basketBooks = action.payload
        state.error = undefined
      },
    )

    builder.addCase(
      loadBasketBooks.pending,
      (state: BookSliceState, action) => {
        state.error = undefined
      },
    )

    builder.addCase(loadBasketBooks.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // add book to basket
    // @ts-ignore
    builder.addCase(
      addBookToBasket.fulfilled,
      (state: BookSliceState, action: PayloadAction<BasketBook[]>) => {
        state.basketBooks = action.payload
        state.error = undefined
      },
    )

    builder.addCase(
      addBookToBasket.pending,
      (state: BookSliceState, action) => {
        state.error = undefined
      },
    )

    builder.addCase(addBookToBasket.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })

    // remove book to basket
    // @ts-ignore
    builder.addCase(
      removeBookFromBasket.fulfilled,
      (state: BookSliceState, action: PayloadAction<BasketBook[]>) => {
        state.basketBooks = action.payload
        state.error = undefined
      },
    )

    builder.addCase(
      removeBookFromBasket.pending,
      (state: BookSliceState, action) => {
        state.error = undefined
      },
    )

    builder.addCase(removeBookFromBasket.rejected, (state, action) => {
      state.loadedNewBooks = true
      state.error = action.payload
    })
  },
})

export const { setFilters, setBook, setBooksToBasket } = bookSlice.actions
export default bookSlice
